import {
    GridUIContextActions,
    GridUIContextValue,
} from "features/allocations/contexts/GridUIContext";
import React, {
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";

import Close from "shared/components/utils/icons/Close";
import Loader from "shared/components/feedback/loading/Loader";
import PropTypes from "prop-types";
import TimeOffOverlay from "features/allocations/components/grid/employee/TimeOffOverlay";
import moment from "moment";
import { useTimeOff } from "features/allocations/api/useTimeOff";
import { CloseWrapper, ContentWrapper } from "./PillTooltip.styles";

const DATA_STATES = {
    READY: "ready",
    LOADING: "loading",
    ERROR: "error",
    BLANK: "blank",
};

const getTooltipPositionStyles = (pillElRect, tooltipElRect, safeArea) => {
    const styles = {
        position: "absolute",
        zIndex: 99,
    };

    // available space above, below, left, and right measured as multiples of tooltip height

    const pillTopMeasuringPoint = pillElRect.top - 10;
    const spaceAbove =
        Math.abs(safeArea.top - pillTopMeasuringPoint) / tooltipElRect.height;

    const pillBottomMeasuringPoint = pillElRect.bottom + 10;
    const spaceBelow =
        Math.abs(safeArea.bottom - pillBottomMeasuringPoint) /
        tooltipElRect.height;

    // if tooltip fits in space above, put it above; otherwise, put it above or below depending on where there is more space
    styles.top =
        globalThis.scrollY +
        (spaceAbove >= 1 || spaceAbove >= spaceBelow
            ? pillTopMeasuringPoint - tooltipElRect.height
            : pillBottomMeasuringPoint);

    const pillHorzMeasuringPoint = pillElRect.left + pillElRect.width / 2;
    const spaceLeft =
        Math.abs(safeArea.left - pillHorzMeasuringPoint) / tooltipElRect.width;
    const spaceRight =
        Math.abs(safeArea.right - pillHorzMeasuringPoint) / tooltipElRect.width;

    // if tooltip fits in space on the right, put it on the right; otherwise, put it on the right or left depending on where there is more space
    styles.left =
        globalThis.scrollX +
        (spaceRight >= 1 || spaceRight >= spaceLeft
            ? pillHorzMeasuringPoint
            : pillHorzMeasuringPoint - tooltipElRect.width);

    return styles;
};

const PillTooltipContent = ({ pillElRef, children }) => {
    const { toggleTooltip, getSafeArea } = useContext(GridUIContextActions);
    const [positionStyles, setPositionStyles] = useState({});
    const elRef = useRef();

    useEffect(() => {
        const clickHandler = (event) => {
            if (!elRef.current.contains(event.target)) {
                toggleTooltip(false);
            }
        };

        const keyHandler = (event) => {
            if (event.key === "Escape") {
                toggleTooltip(false);
            }
        };

        document.addEventListener("click", clickHandler);
        document.addEventListener("keydown", keyHandler);

        return () => {
            document.removeEventListener("click", clickHandler);
            document.removeEventListener("keydown", keyHandler);
        };
    }, [toggleTooltip]);

    useLayoutEffect(() => {
        if (pillElRef.current && elRef.current) {
            setPositionStyles(
                getTooltipPositionStyles(
                    pillElRef.current.getBoundingClientRect(),
                    elRef.current.getBoundingClientRect(),
                    getSafeArea()
                )
            );
        }
    }, [children, getSafeArea, pillElRef]);

    return (
        <div className="tooltip-overlay" ref={elRef} style={positionStyles}>
            {children}
        </div>
    );
};

PillTooltipContent.propTypes = {
    pillElRef: PropTypes.object,
    children: PropTypes.node,
};

const PillTooltip = () => {
    const { getTimeOff } = useTimeOff();

    const { tooltipRequest } = useContext(GridUIContextValue);
    const { toggleTooltip } = useContext(GridUIContextActions);

    const [dataState, setDataState] = useState(DATA_STATES.BLANK);
    const [tooltipData, setTooltipData] = useState(null);

    useEffect(() => {
        if (!tooltipRequest) {
            setTooltipData(null);
            setDataState(DATA_STATES.BLANK);
            return;
        }

        let validRequest = true;

        const { employeeData, date, allocatedHours, availableHours } =
            tooltipRequest;

        setDataState(DATA_STATES.LOADING);

        const daysOfTheWeek = [];
        for (let index = 0; index < 5; index++) {
            daysOfTheWeek.push(
                moment(date).add(index, "days").format("YYYY-MM-DD")
            );
        }

        const dateFrom = moment(date).format("YYYY-MM-DD");
        const dateTo = moment(date).add(6, "days").format("YYYY-MM-DD");

        const fetchTimeOff = async () => {
            try {
                const timeOffData = await getTimeOff({
                    userId: employeeData.userId,
                    dateFrom: dateFrom,
                    dateTo: dateTo,
                });
                
                if (validRequest) {
                    setTooltipData({
                        employeeData,
                        timeOffData,
                        daysOfTheWeek,
                        allocatedHours,
                        availableHours,
                    });
                    setDataState(DATA_STATES.READY);
                }
            } catch {
                if (validRequest) {
                    setTooltipData(null);
                    setDataState(DATA_STATES.ERROR);
                }
            }
        };

        fetchTimeOff();

        return () => {
            validRequest = false;
        };
    }, [tooltipRequest, getTimeOff]);

    return tooltipRequest ? (
        <PillTooltipContent pillElRef={tooltipRequest.pillElRef}>
            {dataState === DATA_STATES.READY && (
                <>
                    <CloseWrapper
                        onClick={(event) => {
                            event.stopPropagation();
                            toggleTooltip(false);
                        }}
                    >
                        <Close color={globalThis.config.colors.white} size="8" />
                    </CloseWrapper>

                    <ContentWrapper>
                        <TimeOffOverlay
                            week={tooltipData.daysOfTheWeek}
                            timeOffData={tooltipData.timeOffData}
                            employeeData={tooltipData.employeeData}
                            allocatedHours={tooltipData.allocatedHours}
                            availableHours={tooltipData.availableHours}
                        />
                    </ContentWrapper>
                </>
            )}

            {dataState === DATA_STATES.LOADING && (
                <Loader height="18px" width="18px" />
            )}

            {dataState === DATA_STATES.Error && "Error"}
        </PillTooltipContent>
    ) : (
        <></>
    );
};

PillTooltip.propTypes = {};

export default PillTooltip;
